/**
 * Shared CSV shape for parametric-style exports: one row per simulation result,
 * matching ParametricAnalysis SelectAxisAndChartType.
 */

import { resolveRunResultEcmDisplayCell } from "utils/runResultUpgradeDisplay";
import { getTotalCostOfOwnership } from "utils/parametric";

export const getParametricTcoColumnHeader = (timeHorizon) => `Total Cost of Ownership (${timeHorizon} y)`;

/** ECM columns for community CSV only (uses `ecmInputMap` + run payload fallbacks). */
const buildParametricResultRowForCommunity = (resultId, values, ecmInputMap) => {
    const newRow = [resultId];

    ecmKeysForCSV.forEach((ecmKey) => {
        const { listOfUpgrades = "", upgradeIds = "", upgradesPresent } = values || {};
        const hasPresent =
            upgradesPresent && typeof upgradesPresent === "object" && Object.keys(upgradesPresent).length > 0;
        const hasAny =
            (typeof listOfUpgrades === "string" && listOfUpgrades.trim() !== "") ||
            (typeof upgradeIds === "string" && upgradeIds.trim() !== "") ||
            hasPresent;
        if (!hasAny) {
            newRow.push("None");
        } else {
            newRow.push(resolveRunResultEcmDisplayCell(ecmKey, values, ecmInputMap));
        }
    });

    resultsKeysForCSV.forEach((resKey) => {
        newRow.push(values[resKey]?.toFixed(2) || "");
    });

    return newRow;
};

export const ecmKeysForCSV = [
    "ceilingWithAttic",
    "cathedral",
    "wall",
    "expFloor",
    "floorHeader",
    "basementWall",
    "basementSlab",
    "crawlspace",
    "slab",
    "window",
    "skylights",
    "door",
    "airTightness",
    "ventilation",
    "heatingCooling",
    "spaceHeating",
    "secondaryHeating",
    "spaceCooling",
    "primaryHotWater",
    "dwhr",
    "generation",
    "transportation",
];

export const resultsKeysForCSV = [
    "percentSavings",
    "aecGJ",
    "pvGJ",
    "netGJ",
    "spaceHeatingGJ",
    "hotWaterGJ",
    "baseloadGJ",
    "spaceCoolingGJ",
    "ventilationGJ",
    "elecGJ",
    "ngGJ",
    "propaneGJ",
    "oilGJ",
    "woodGJ",
    "h2kHeatLossW",
    "h2kHeatGainW",
    "f280HeatLossW",
    "f280HeatGainW",
    "auxHeatingMJ",
    "grossHeatLossGJ",
    "tedi",
    "meui",
    "eui",
    "totalEmissionsTCO2",
    "heatLossReductionVSERSRef",
    "overallEnergyPerfVSERSRef",
    "totalUpgradeCost",
    "totalOperatingCost",
];

export const parametricCsvHeaderRows = [
    [
        "ID",
        "Ceiling With Attic",
        "Cathedral",
        "Above Grade Walls / Garage wall",
        "Exposed Floors",
        "Floor Header",
        "Foundation Wall",
        "Basement Slab",
        "Crawlspace",
        "Slab-on-grade",
        "Windows & Sliding Glass Doors",
        "Skylights",
        "Door",
        "Air Tightness",
        "Ventilation",
        "Heating & Cooling",
        "Space Heating",
        "Secondary Heating",
        "Cooling & Heat Pumps",
        "Primary Hot Water",
        "DWHR",
        "Generation",
        "Transportation",
        "Energy Savings (%)",
        "Annual Energy Consumption (GJ)",
        "Annual Energy Production (GJ)",
        "Net Energy Consumption (GJ)",
        "Space Heating (GJ)",
        "Hot Water (GJ)",
        "Baseloads (GJ)",
        "Space Cooling (GJ)",
        "Ventilation (GJ)",
        "Electricity (GJ)",
        "Natural Gas (GJ)",
        "Propane (GJ)",
        "Oil (GJ)",
        "Wood (GJ)",
        "H2k Heat Loss (W)",
        "H2k Heat Gain (W)",
        "F280 Heat Loss (W)",
        "F280 Heat Gain (W)",
        "Auxiliary Heating Energy (MJ)",
        "Gross Heat Loss (GJ)",
        "TEDI (kWh/m2)",
        "MEUI (kWh/m2)",
        "EUI (kWh/m2)",
        "Total Emissions (tCO2)",
        "Heat Loss Reduction vs ERS Ref",
        "Overall Energy Performance Improvement vs ERS Ref",
        "Total Capital Cost ($)",
        "Annual Energy Cost ($)",
    ],
];

export const communityCsvHeaderRows = [
    ["Category", "Model Name", "Direction", "Duplicates", ...parametricCsvHeaderRows[0]],
];

/**
 * Parametric analysis CSV rows: ECM labels come from `listOfUpgrades` only (explicit upgrade build path).
 */
export const buildParametricResultRow = (resultId, values, timeHorizon = 1) => {
    const newRow = [resultId];

    ecmKeysForCSV.forEach((ecmKey) => {
        const { listOfUpgrades = "" } = values || {};

        if (listOfUpgrades === "") {
            newRow.push("None");
        } else {
            let ecmLabel = "None";
            listOfUpgrades.split(";").forEach((upgrade) => {
                const [type, label] = upgrade.split("=>");

                if (type === ecmKey) {
                    ecmLabel = label.replace(/,/g, "");
                }
            });

            newRow.push(ecmLabel);
        }
    });

    resultsKeysForCSV.forEach((resKey) => {
        newRow.push(values[resKey]?.toFixed(2) || "");
    });

    const { totalUpgradeCost = 0, totalOperatingCost = 0 } = values || {};
    const totalCostOfOwnership = getTotalCostOfOwnership(totalUpgradeCost, timeHorizon, totalOperatingCost);
    newRow.push(totalCostOfOwnership?.toFixed(2) || "");

    return newRow;
};

/**
 * Community result keys: modelId_rest where rest splits on "-" into direction, chunkNumber, upgradeId…
 */
export const parseCommunityResultKeyParts = (key) => {
    const parts = key.split("_");
    const modelId = parts[0] ?? "";
    const directionAndId = parts.length > 1 ? parts.slice(1).join("_") : "";
    const dirParts = directionAndId.split("-");
    const direction = dirParts[0] ?? "";
    const chunkNumber = dirParts[1] ?? "";
    const upgradeId = dirParts.length > 2 ? dirParts.slice(2).join("-") : "";

    return { modelId, direction, chunkNumber, upgradeId, directionAndId };
};

const baseModelIdFromResultsKey = (resultsId) => {
    const { modelId } = parseCommunityResultKeyParts(resultsId);
    return modelId || null;
};

const lookupModelName = (modelId, allModels) => allModels?.find((m) => m.value === modelId)?.label ?? "";

const lookupDuplicateCount = (modelId, direction, communityModelCountsMap) => {
    const mapKey = `${modelId}_${direction}`;
    const n = communityModelCountsMap?.[mapKey];
    if (n === undefined || n === null) {
        return "";
    }
    return n;
};

const categoryLabelWithPackage = (category, values, allPackages = {}) => {
    const packageId = values?.upgradePackageId;
    if (packageId == null || packageId === "") {
        return category;
    }
    const packageName = allPackages?.[packageId]?.name;
    return packageName ? `${category}: ${packageName}` : category;
};

const buildCommunityResultRow = (category, key, values, { allModels, communityModelCountsMap, allPackages, ecmInputMap }) => {
    const { modelId, direction } = parseCommunityResultKeyParts(key);
    const modelName = lookupModelName(modelId, allModels);
    const duplicates = lookupDuplicateCount(modelId, direction, communityModelCountsMap);
    const categoryLabel = categoryLabelWithPackage(category, values, allPackages);

    const [, ...parametricRest] = buildParametricResultRowForCommunity(key, values, ecmInputMap);

    return [categoryLabel, modelName, direction, duplicates, key, ...parametricRest];
};

const shouldIncludeResultForModels = (resultsId, selectedModelIds) => {
    if (!selectedModelIds?.length) {
        return true;
    }
    const baseModelId = baseModelIdFromResultsKey(resultsId);
    if (!baseModelId) {
        return true;
    }
    return selectedModelIds.includes(baseModelId);
};

/**
 * Parametric analysis: runResults is { [batchOrChunkId]: { results: { [resultKey]: values } } }.
 */
export const buildParametricCsvFromNestedRunResults = (runResults, timeHorizon = 1) => {
    const csvDataToExport = [...parametricCsvHeaderRows.map((row) => [...row])];
    csvDataToExport[0].push(getParametricTcoColumnHeader(timeHorizon));

    Object.entries(runResults || {}).forEach(([, runResultsData]) => {
        const { results = {} } = runResultsData || {};

        Object.entries(results).forEach(([key, values]) => {
            csvDataToExport.push(buildParametricResultRow(key, values, timeHorizon));
        });
    });

    return csvDataToExport;
};

/**
 * Community results: merge whole-package runs and by-measure runs, one row per result.
 * Leading columns: Category, Model Name, Direction (label), Duplicates, then the parametric columns (ID = result key).
 */
export const buildCommunityAllResultsCsv = (
    runData,
    selectedModelIds,
    { allModels = [], communityModelCountsMap = {}, allPackages = {}, ecmInputMap } = {}
) => {
    const csvDataToExport = [...communityCsvHeaderRows.map((row) => [...row])];
    const { runResults = {}, runResultsByMeasure = [] } = runData || {};

    Object.entries(runResults).forEach(([, batchDetails]) => {
        const { results = {} } = batchDetails || {};

        Object.entries(results).forEach(([key, values]) => {
            if (!shouldIncludeResultForModels(key, selectedModelIds)) {
                return;
            }
            csvDataToExport.push(
                buildCommunityResultRow("Whole Package", key, values, {
                    allModels,
                    communityModelCountsMap,
                    allPackages,
                    ecmInputMap,
                })
            );
        });
    });

    (Array.isArray(runResultsByMeasure) ? runResultsByMeasure : []).forEach((doc) => {
        const { results = {} } = doc || {};

        Object.entries(results).forEach(([key, values]) => {
            if (!shouldIncludeResultForModels(key, selectedModelIds)) {
                return;
            }
            csvDataToExport.push(
                buildCommunityResultRow("By Measure", key, values, {
                    allModels,
                    communityModelCountsMap,
                    allPackages,
                    ecmInputMap,
                })
            );
        });
    });

    return csvDataToExport;
};
